import styles from './BasketCard.module.scss'
import { useRef, useState } from 'react'
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper/modules'

import 'swiper/css'
import 'swiper/css/navigation'

import { TrashBasket } from 'shared/assets'
import {
  useUpdateOptionByIdMutation,
} from '../../../entities/Order/api/apiCartSlice'
import { useContext } from 'react'
import { SupplierContext } from 'context/SupplierContext'
import { showNotification, ToastVersions } from 'libraries/toastify'

const BasketCard = ({ data, optionGroup, refetch, warehouses = [] }: any) => {
  const { is_supplier } = useContext(SupplierContext)

  const [updateOption] = useUpdateOptionByIdMutation()

  // Swiper state
  const swiperRef = useRef<SwiperRef>(null)
  const hasImages = data?.images && data.images.length > 0
  const imageList = hasImages ? data.images : []

  const removeFromCartGA = (qty: number) => {
    if (typeof window.gtag !== 'function') return
    window.gtag('event', 'remove_from_cart', {
      currency: 'UAH',
      value: data?.price,
      items: [{ item_id: data?.id, item_name: data?.name, quantity: qty, price: data?.price }],
    })
  }

  const updateQuantity = async (group: any, newQty: number) => {
    if (newQty < 1) {
      // Delete this size option
      try {
        if (group.mainOption) {
          await updateOption({ cart_item_option_value_id: group.mainOption.id, quantity: 0 }).unwrap()
          removeFromCartGA(group.mainOption.quantity)
        }
        if (group.extraOption) {
          await updateOption({ cart_item_option_value_id: group.extraOption.id, quantity: 0 }).unwrap()
          removeFromCartGA(group.extraOption.quantity)
        }
        refetch()
      } catch (e: any) {
        console.error('Error deleting option:', e)
        const errorMessage = e?.data?.message || e?.data?.error || 'Помилка при видаленні товару'
        showNotification(ToastVersions.error, errorMessage)
      }

      return
    }

    const currentTotal = group.totalQuantity
    const delta = newQty - currentTotal

    try {
      if (delta > 0) {
        // Increase quantity - backend will handle warehouse selection
        if (group.mainOption) {
          await updateOption({
            cart_item_option_value_id: group.mainOption.id,
            quantity: group.mainOption.quantity + 1,
          }).unwrap()
        } else if (group.extraOption) {
          await updateOption({
            cart_item_option_value_id: group.extraOption.id,
            quantity: group.extraOption.quantity + 1,
          }).unwrap()
        }
      } else {
        // Decrease quantity
        if (group.extraOption && group.extraOption.quantity > 0) {
          await updateOption({
            cart_item_option_value_id: group.extraOption.id,
            quantity: group.extraOption.quantity - 1,
          }).unwrap()
        } else if (group.mainOption && group.mainOption.quantity > 0) {
          await updateOption({
            cart_item_option_value_id: group.mainOption.id,
            quantity: group.mainOption.quantity - 1,
          }).unwrap()
        }
      }

      refetch()
    } catch (e: any) {
      console.error('Error updating quantity:', e)

      // Extract error message from response
      const errorMessage = e?.data?.message || e?.data?.error || 'Недостатньо товару на складі'
      showNotification(ToastVersions.error, errorMessage)

      // Refetch to get the correct quantity from server
      refetch()
    }
  }

  const deleteGroup = async (group: any) => {
    try {
      if (group.mainOption) {
        await updateOption({ cart_item_option_value_id: group.mainOption.id, quantity: 0 }).unwrap()
        removeFromCartGA(group.mainOption.quantity)
      }
      if (group.extraOption) {
        await updateOption({ cart_item_option_value_id: group.extraOption.id, quantity: 0 }).unwrap()
        removeFromCartGA(group.extraOption.quantity)
      }
      refetch()
    } catch (e: any) {
      console.error('Error deleting option:', e)
      const errorMessage = e?.data?.message || e?.data?.error || 'Помилка при видаленні товару'
      showNotification(ToastVersions.error, errorMessage)
      refetch()
    }
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.wrapper_image}>
        {hasImages && imageList.length > 0 ? (
          <Swiper
            ref={swiperRef}
            modules={[Navigation]}
            navigation
            loop={imageList.length > 1}
            className={styles.imageSwiper}
          >
            {imageList.map((image: any, idx: number) => (
              <SwiperSlide key={idx}>
                <div className={styles.imageLayout}>
                  <img src={image.original} alt={`${data.name} - ${idx + 1}`} />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <div className={styles.imageLayout}>
            <img src={data.thumb} alt={data.name} />
          </div>
        )}
      </div>
      <div className={styles.wrapper_content}>
        {/* Product name with trash icon */}
        <div className={styles.header}>
          <h3 className={styles.productName}>{data.name}</h3>
          <div className={styles.trashIcon} onClick={() => deleteGroup(optionGroup)}>
            <TrashBasket />
          </div>
        </div>

        {/* Price */}
        <div className={styles.price}>{data.special ?? data.price}</div>

        {/* Size and Quantity with 2px gap */}
        <div className={styles.sizeQuantityGroup}>
          {/* Size info */}
          <div className={styles.sizeInfo}>
            {optionGroup.value}
          </div>

          {/* Quantity controls */}
          <div className={styles.quantityControl}>
            <span className={styles.quantityLabel}>Кількість:</span>
            <div className={styles.quantityButtons}>
              <button
                className={styles.quantityBtn}
                onClick={() => updateQuantity(optionGroup, optionGroup.totalQuantity - 1)}
              >
                −
              </button>
              <span className={styles.quantityValue}>{optionGroup.totalQuantity}</span>
              <button
                className={styles.quantityBtn}
                onClick={() => updateQuantity(optionGroup, optionGroup.totalQuantity + 1)}
              >
                +
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BasketCard
